import React from 'react'
import {connect} from 'react-redux'
import {fromJS} from 'immutable'
import verge from 'verge'

import {
  ROLES,
  VIEWPORT_MAX_WIDTH_PHONE,
  CONVERSATION_PANE_HEIGHT,
  CONVERSATION_PANE_STATUSES
} from '../shared/config/constants'

import getUser from '../shared/data/getters/user'

import calculateConversationContentHeight from './helpers/calculate_conversation_content_height'
import setReadMessageCount from '../shared/data/setters/set_conversation_ref_read_message_count'

import ConversationUserHeader from './conversation_user_header'
import ConversationContent from '../shared/conversation/content'
import ConversationActions from '../shared/conversation/actions'
import ConversationMessageInformer from './conversation_message_informer'
import ExpertInfoMessageInformer from './expert_info_message_informer'
import SessionInfoMessageInformer from './session_info_message_informer'
import ConversationSecondaryHeader from './conversation_secondary_header'
import ExpertConversationSessionProgressAction from './expert_conversation_session_progress_action'

class ExpertConversation extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      isHidden: this.props.isHidden || false,
      isActive: this.props.isActive
    }

    this.adjustContentHeight = this.adjustContentHeight.bind(this)
  }

  componentWillMount () {
    if (verge.viewportW() <= VIEWPORT_MAX_WIDTH_PHONE) {
      window.addEventListener('resize', this.adjustContentHeight)
    }

    if (this.props.conversation.get('targetId')) {
      this.props.getUser(this.props.conversation.get('targetId'))
    }

    if (this.shouldUpdateReadMessageCount(this.props)) {
      this.props.setReadMessageCount(
        this.props.currentUserUid,
        this.props.conversation.get('conversationId'),
        this.props.totalMessageCount
      )
    }
  }

  componentWillReceiveProps (nextProps) {
    if (this.shouldUpdateReadMessageCount(nextProps)) {
      this.props.setReadMessageCount(
        nextProps.currentUserUid,
        nextProps.conversation.get('conversationId'),
        nextProps.totalMessageCount
      )
    }
  }

  componentDidMount () {
    this.adjustContentHeight()
  }

  adjustContentHeight () {
    const contentHeight = calculateConversationContentHeight(
      CONVERSATION_PANE_HEIGHT,
      this.props.headerHeight, this.props.secondaryHeaderHeight,
      this.props.actionsHeight
    )

    this.setState({contentHeight})
  }

  shouldShowBody () {
    const {OPEN} = CONVERSATION_PANE_STATUSES
    const status = this.props.conversation.get('status')

    return status === OPEN ? true : false
  }

  shouldUpdateReadMessageCount (nextProps) {
    return (
      nextProps.currentUserUid &&
      this.isConversationOpen(nextProps.conversation.get('status')) &&
      nextProps.totalMessageCount > nextProps.readMessageCount
    )
  }

  isConversationOpen (status = this.props.conversation.get('status')) {
    const {OPEN} = CONVERSATION_PANE_STATUSES

    return status === OPEN ? true : false
  }

  isConversationActive () {
    if (this.props.currentUser.get('role') !== ROLES.EXPERT) return false

    if (this.props.isUserIgnored) return false

    return true
  }

  renderSessionInfoMessage () {
    return (
      <SessionInfoMessageInformer
        user={this.props.user.toJS()}
        sessionId={this.props.activeSessionId}/>
    )
  }

  renderExpertInfoMessage () {
    return (
      <ConversationMessageInformer>
        <div>
          You are in expert mode.<br/>
          You can send message to user, but user won't be able to respond back.<br/>
          Otherwise, you can switch to writer mode to enable chat with everyone.
        </div>
      </ConversationMessageInformer>
    )
  }

  renderUserInfoMessage () {
    return (
      <ExpertInfoMessageInformer user={this.props.user.toJS()}/>
    )
  }

  renderActionWarningMessage () {
    if (this.props.isUserIgnored) {
      return 'You have ignored this user.'
    }
  }

  renderConversationContentMessage () {
    if (this.props.currentUser.get('role') === ROLES.EXPERT) {
      if (this.props.activeSessionId) return this.renderSessionInfoMessage()
      else return this.renderExpertInfoMessage()
    }
    else if (this.props.user.get('role') === ROLES.EXPERT) {
      if (this.props.activeSessionId) return this.renderSessionInfoMessage()
      else return this.renderUserInfoMessage()
    }
  }

  renderHeader () {
    return (
      <ConversationUserHeader
        ref='conversationHeader'
        conversation={this.props.conversation}/>
    )
  }

  renderSecondaryHeader () {
    if (!this.shouldShowBody()) return false

    if (this.props.currentUser.get('role') !== ROLES.EXPERT) {
      return (
        <ConversationSecondaryHeader ref='secondaryHeader' height={this.props.secondaryHeaderHeight}>
          <ExpertConversationSessionProgressAction
            sessionId={this.props.activeSessionId}
            expert={this.props.user.toJS()}
            classNames='conversation-secondary-header-button'/>
        </ConversationSecondaryHeader>
      )
    }
  }

  renderConversationActions () {
    return (
      <ConversationActions
        ref="conversationActions"
        isActive={this.isConversationActive()}
        height={this.props.actionsHeight}
        currentUser={this.props.currentUser}
        otherUser={this.props.user}
        warningMessage={this.renderActionWarningMessage()}
        onNewMessageInputFocus={this.props.onConversationFocus}
        onNewMessageInputBlur={this.props.onConversationBlur}
        conversationContainerId={this.props.conversation.get('conversationId')}/>
    )
  }

  renderBody () {
    return (
      <div className='conversation-body' style={{display: this.shouldShowBody() ? '' : 'none'}}>
        <ConversationContent
          ref='conversationContent'
          height={this.state.contentHeight}
          conversationContainerId={this.props.conversation.get('conversationId')}
          currentUser={this.props.currentUser}
          otherUser={this.props.user}
          contentMessage={this.renderConversationContentMessage()}
          shouldLoadPreviousMessages={false}
          isRichMessageContentEnabled={true}/>
        {this.renderConversationActions()}
      </div>
    )
  }

  render () {
    return (
      <div ref='conversation' className='conversation expert'>
        {this.renderHeader()}
        {this.renderSecondaryHeader()}
        {this.renderBody()}
      </div>
    )
  }
}

ExpertConversation.defaultProps = {
  isActive: false,
  actionsHeight: 41,
  headerHeight: 42,
  secondaryHeaderHeight: 54,
  onConversationActive: () => {}
}

const mapStateToProps = (state, ownProps) => {
  return {
    currentUser: state.get('currentUser', fromJS({})),
    currentUserUid: state.getIn(['currentUser', 'uid']),
    user: state.getIn(['users', ownProps.conversation.get('targetId')]),
    totalMessageCount: state.getIn(['conversations', '_counts', ownProps.conversation.get('conversationId'), 'total']),
    readMessageCount: state.getIn(['conversations', '_counts', ownProps.conversation.get('conversationId'), 'read']),
    isUserIgnored: state.getIn(['currentUser', 'ignored', ownProps.conversation.get('targetId')]),
    activeSessionId: state.getIn(['sessions', '_refs', ownProps.conversation.get('targetId'), 0, 'sessionId'])
  }
}

const mapDispatchToProps = () => {
  return {
    getUser: getUser,
    setReadMessageCount: setReadMessageCount
  }
}

const ExpertConversationContainer = connect(
  mapStateToProps, mapDispatchToProps
)(ExpertConversation)

export default ExpertConversationContainer



/** WEBPACK FOOTER **
 ** ./modules/chat_ui/expert_conversation.js
 **/